import { HttpClient, HttpContext, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, map, Observable, throwError } from "rxjs";
import { SendNotificationPayload } from "src/app/modules/notifications/notifications.model";
import { NotificationCounters, NotificationItemType, NotificationSection } from "../state/notifications/notifications.model";
import { ToastAlertService } from "src/app/shared/utils/toast.service";
import { ApiEndpointService } from "./api-endpoint.service";
import { SKIP_FORBIDDEN_REDIRECT, SKIP_UNAUTHORIZED_REDIRECT } from "../http-interceptor/http-context-tokens";


@Injectable({
  providedIn: "root"
})
export class NotificationsService {

  constructor(private http: HttpClient, private toastService: ToastAlertService) { }

  public fetchNotifications(payload: { pageNumber: number, limit: number }): Observable<any> {
    const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.NOTIFICATIONS);
    const urlWithQueryParams = url + `?pageNumber=${payload.pageNumber}&limit=${payload.limit}`
    return this.http.get(urlWithQueryParams).pipe(
      map((response: any): any => {
        return response.data;
      }),
      catchError((fault: HttpErrorResponse) => {
        this.toastService.showToast(fault?.error?.message || 'Error while fetching notifications data', 'error');
        return throwError(() => fault);
      })
    );
  }

  public sendPlatformNotification(payload: SendNotificationPayload): Observable<any> {
    const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.NOTIFICATIONS + "/platform");
    return this.http.post(url, payload).pipe(
      map((response: any): any => {
        return response;
      }),
      catchError((fault: HttpErrorResponse) => {
        this.toastService.showToast(fault?.error?.message || 'Error while sending the platform notification', 'error');
        return throwError(() => fault);
      })
    );
  }

  public fetchNotificationCount(): Observable<any> {
    const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.NOTIFICATIONS + "/count");
    return this.http.get(url).pipe(
      map((response: any): any => {
        if (response?.data) {
          return response.data;
        }
      }),
      catchError((fault: HttpErrorResponse) => {
        // this.toastService.showToast(fault?.error?.message || 'Error while fetching notifications count', 'error');
        return throwError(() => fault);
      })
    );
  }

  public markAllReadNotifications(): Observable<any> {
    const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.NOTIFICATIONS + "/mark-all-read");
    return this.http.patch(url, null).pipe(
      map((response: any): any => {
        return response;
      }),
      catchError((fault: HttpErrorResponse) => {
        // this.toastService.showToast(fault?.error?.message || 'Error while mark all read notifications', 'error');
        return throwError(() => fault);
      })
    );
  }

  // SAN-1428 — Notifications BRD v1.1 endpoints. All return 403 unless the
  // tenant has notification_centre_enabled, so callers must check the flag
  // first. Errors stay silent like fetchNotificationCount(): a failed
  // counter refresh must never toast on every poll. The 403 of a tenant with
  // the flag off is expected, so it skips the global toast + redirect to '/'.
  private silentForbidden() {
    return { context: new HttpContext().set(SKIP_FORBIDDEN_REDIRECT, true) };
  }

  // SAN-1729 — view tracking also runs on public pages, so a 401 there means
  // "no session", not "session expired": no logout + redirect to login.
  private silentTracking() {
    return {
      context: new HttpContext()
        .set(SKIP_FORBIDDEN_REDIRECT, true)
        .set(SKIP_UNAUTHORIZED_REDIRECT, true),
    };
  }

  public getCounters(): Observable<NotificationCounters> {
    const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.NOTIFICATIONS + "/counters");
    return this.http.get(url, this.silentForbidden()).pipe(
      map((response: any): NotificationCounters => response?.data),
      catchError((fault: HttpErrorResponse) => throwError(() => fault))
    );
  }

  public recordSectionVisit(section: NotificationSection, leftAt: Date): Observable<any> {
    return this.http.post(this.sectionVisitUrl(section), { leftAt: leftAt.toISOString() }, this.silentTracking()).pipe(
      catchError((fault: HttpErrorResponse) => throwError(() => fault))
    );
  }

  // Page-unload fallback for recordSectionVisit(). HttpClient requests are
  // cancelled when the tab closes; a keepalive fetch survives it. Auth is the
  // accessToken cookie, so credentials: 'include' is all it needs (the same
  // x-hostname header the interceptor adds is set by hand here).
  public recordSectionVisitOnUnload(section: NotificationSection, leftAt: Date): void {
    try {
      fetch(this.sectionVisitUrl(section), {
        method: 'POST',
        keepalive: true,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-hostname': location.hostname },
        body: JSON.stringify({ leftAt: leftAt.toISOString() }),
      }).catch(() => { });
    } catch (e) { }
  }

  public recordItemView(itemType: NotificationItemType, itemUuid: string): Observable<any> {
    const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.NOTIFICATIONS + "/item-views");
    return this.http.post(url, { itemType, itemUuid }, this.silentTracking()).pipe(
      catchError((fault: HttpErrorResponse) => throwError(() => fault))
    );
  }

  // SAN-1487 (EX-18) — web push: whether it is offered, and this device's subscription.
  public getPushConfig(): Observable<{ available: boolean; publicKey: string | null; subscribed: boolean }> {
    const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.NOTIFICATIONS + "/push/config");
    return this.http.get<any>(url).pipe(
      map((res) => res?.data ?? { available: false, publicKey: null, subscribed: false }),
      catchError((fault: HttpErrorResponse) => throwError(() => fault))
    );
  }

  public savePushSubscription(subscription: PushSubscription): Observable<any> {
    const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.NOTIFICATIONS + "/push/subscriptions");
    const json = subscription.toJSON();
    return this.http.post(url, { endpoint: json.endpoint, keys: json.keys }).pipe(
      catchError((fault: HttpErrorResponse) => throwError(() => fault))
    );
  }

  public removePushSubscription(endpoint: string): Observable<any> {
    const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.NOTIFICATIONS + "/push/subscriptions");
    return this.http.request('delete', url, { body: { endpoint } }).pipe(
      catchError((fault: HttpErrorResponse) => throwError(() => fault))
    );
  }

  public markRead(uuid: string): Observable<any> {
    const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.NOTIFICATIONS + `/${uuid}/read`);
    return this.http.patch(url, null, this.silentForbidden()).pipe(
      catchError((fault: HttpErrorResponse) => throwError(() => fault))
    );
  }

  public markJobApplicationViewed(applicationUUID: string): Observable<any> {
    const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.JOBS + `applications/${applicationUUID}/viewed`);
    return this.http.patch(url, null, this.silentForbidden()).pipe(
      catchError((fault: HttpErrorResponse) => throwError(() => fault))
    );
  }

  private sectionVisitUrl(section: NotificationSection): string {
    return ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.NOTIFICATIONS + `/sections/${section}/visit`);
  }
}
